import { useCallback, useEffect, useMemo } from 'react';

import { TokenEx, useFormListValidation } from 'core';
import { ICredential, useCreateStripePaymentMethod, useExistingCardCredential } from 'core/Api';

import { CheckoutForms } from '../checkout.types';

type UseCreateCredential = {
  onCredentialChange: (credentialId?: ICredential['uuid']) => void;
};

const useCreateCredential = ({ onCredentialChange }: UseCreateCredential) => {
  const { getTokenizedCard } = TokenEx.useTokenize();

  const { mutateAsync: createCard, isPending: isCreateCardLoading } =
    useCreateStripePaymentMethod();

  const { mutateAsync: createExistingCredential, isPending: isExistingCredentialLoading } =
    useExistingCardCredential();

  const { useGetForm } = useFormListValidation<CheckoutForms>();

  /** Form for the Existing Credentials */
  const existingCredentialsForm = useGetForm('existing_credentials');

  /** The selected Credentials CVV form */
  const cvvForm = useGetForm('existing_credential');

  const credentialId = useMemo(
    () => existingCredentialsForm?.values?.credentialId,
    [existingCredentialsForm?.values?.credentialId]
  );

  const createCredential = useCallback(async () => {
    // Always attempt to get the tokenizedCard
    const card = await getTokenizedCard();

    // Guards
    if (!card && !credentialId) {
      throw new Error('Select a valid payment method');
    }

    // If the user does not has a credential ID, create a new card
    if (!credentialId) {
      return createCard(card);
    }

    /** Run Validation For Existing Credential Forms  */
    existingCredentialsForm?.validate();
    cvvForm?.validate();

    if (credentialId) {
      if (!card?.token) {
        cvvForm?.setFieldValue('isCvvValid', false);
        throw new Error('Invalid CVV');
      }
      return createExistingCredential(credentialId);
    } else {
      throw new Error('Select a valid payment method');
    }
  }, [
    createCard,
    createExistingCredential,
    getTokenizedCard,
    credentialId,
    cvvForm,
    existingCredentialsForm,
  ]);

  const isCreateCredentialLoading = useMemo(
    () => isCreateCardLoading || isExistingCredentialLoading,
    [isCreateCardLoading, isExistingCredentialLoading]
  );

  useEffect(() => {
    onCredentialChange(credentialId);
  }, [onCredentialChange, credentialId]);

  return {
    createCredential,
    isCreateCredentialLoading,
  };
};

export { useCreateCredential };
